import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Shield } from 'lucide-react';
import api, { Role } from '../../services/api';

export default function AdminRoles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({ name: '', permissions: [] as string[] });

  const availablePermissions = [
    'leads.view', 'leads.manage',
    'deals.view', 'deals.manage', 'deals.own',
    'customers.view', 'customers.manage',
    'reports.view', 'reports.export',
    'users.manage', 'settings.manage'
  ];

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await api.getRoles();
        setRoles(data);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editingRole) {
        await api.updateRole(editingRole.id, formData);
      } else {
        await api.createRole(formData);
      }
      const data = await api.getRoles();
      setRoles(data);
      setShowModal(false);
      setEditingRole(null);
      setFormData({ name: '', permissions: [] });
    } catch (error) {
      console.error('Failed to save role:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this role?')) {
      try {
        await api.deleteRole(id);
        setRoles(roles.filter(r => r.id !== id));
      } catch (error) {
        console.error('Failed to delete role:', error);
      }
    }
  };

  const togglePermission = (perm: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm) 
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const openEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({ name: role.name, permissions: role.permissions });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles</h1>
          <p className="text-gray-500 mt-1">Manage user roles and permissions.</p>
        </div>
        <button onClick={() => { setEditingRole(null); setFormData({ name: '', permissions: [] }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <Plus className="w-5 h-5" /> Add Role
        </button>
      </div>

      <div className="grid gap-4">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{role.name}</h3>
                  <p className="text-sm text-gray-500">{role.usersCount} users</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(role)} className="p-2 hover:bg-gray-100 rounded-lg"><Pencil className="w-4 h-4 text-gray-500" /></button>
                {role.name !== 'Admin' && (
                  <button onClick={() => handleDelete(role.id)} className="p-2 hover:bg-gray-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {role.permissions.includes('all') ? (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">All Permissions</span>
              ) : role.permissions.map((perm) => (
                <span key={perm} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  {perm.replace('.', ' ')}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editingRole ? 'Edit Role' : 'Add Role'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Sales Manager" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="grid grid-cols-2 gap-2">
                  {availablePermissions.map((perm) => (
                    <label key={perm} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input type="checkbox" checked={formData.permissions.includes(perm)} onChange={() => togglePermission(perm)} className="rounded text-indigo-600" />
                      <span className="text-sm text-gray-700">{perm.replace('.', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t sticky bottom-0 bg-white">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingRole ? 'Save Changes' : 'Create Role'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
